import { useEffect, useRef, useState } from 'react';
import useRefInView from './useRefInView';

/**
 * useImpressionTracking invokes the tracking callback once when the ref element is in the viewport.
 *
 * Usage:
 *   import useImpressionTracking from './useImpressionTracking';
 *
 *   const ref = useImpressionTracking(() => {
 *     trackViewed()
 *   });
 *
 *   return <div ref={ref}>Hello world!</div>
 */
function useImpressionTracking<T extends Element | null = HTMLDivElement>(
  trackingCallback: () => void,
) {
  const [hasViewed, setHasViewed] = useState(false);
  const containerRef = useRef<T>(null);

  const isInViewport = useRefInView(containerRef);

  useEffect(() => {
    if (!hasViewed && isInViewport) {
      setHasViewed(true);
      trackingCallback();
    }
  }, [hasViewed, isInViewport, trackingCallback]);

  return containerRef;
}

export default useImpressionTracking;
